import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    IconButton,
    LinearProgress,
    Chip,
    useMediaQuery,
    useTheme,
    CircularProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider
} from '@mui/material';
import {
    Person,
    Folder as FolderIcon,
    CheckCircle as CheckCircleIcon,
    Pending,
    PlayCircle as PlayCircleIcon,
    Schedule as ScheduleIcon,
    PauseCircle as PauseCircleIcon,
    MoreHoriz as MoreHorizIcon,
    BarChart,
    Assignment,
    Settings,
    Notifications,
    PersonAdd,
    Edit,
    TaskAlt,
    PlayArrow,
    Refresh,
    Cancel,
    Build,
    Security,
    Warning,
    TrendingUp,
    Assessment,
    School,
    Event,
    GroupWork,
    Work,
    Business,
    Add,
    Update,
    Delete,
    Upload,
    Download,
    Description,
    Info,
    CalendarMonth,
    ArrowForward
} from '@mui/icons-material';
import {
    BarChart as RechartsBarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { Layout } from '@layout/Layout';
import { useAuth } from '@store/authStore';
import { useAppTheme } from '@contexts/ThemeContext';
import { databaseService } from '@services/databaseService';
import { formatDate } from '@utils/dateFormatter';
import { MiniCalendar } from '@/components/Dashboard';
import {
    getUpcomingHolidays,
    getUpcomingBirthdays,
    Holiday,
    Birthday
} from '@/services/DashboardAnalyticsService';

const ProgramManagerDashboard: React.FC = () => {
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    // State for real data
    const [dashboardData, setDashboardData] = useState({
        totalPrograms: 0,
        completedPrograms: 0,
        pendingPrograms: 0,
        inProgressPrograms: 0
    });
    const [loading, setLoading] = useState(true);
    const [recentPrograms, setRecentPrograms] = useState<any[]>([]);
    const [recentActivities, setRecentActivities] = useState<any[]>([]);
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [birthdays, setBirthdays] = useState<Birthday[]>([]);

    // Project status bar chart data
    const projectStatusBarData = [
        { status: 'Completed', value: dashboardData.completedPrograms },
        { status: 'In Progress', value: dashboardData.inProgressPrograms },
        { status: 'Pending', value: dashboardData.pendingPrograms }
    ];

    // Debug log to check theme mode
    console.log('Dashboard - Theme mode:', theme.palette.mode, 'isDarkMode:', isDarkMode);

    // Fetch real data on component mount
    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const stats = await databaseService.getDatabaseStats();

                // Get data from database stats
                setDashboardData({
                    totalPrograms: stats.totalPrograms || 0, // From programs table
                    completedPrograms: stats.completedPrograms || 0, // From programs table where status = 'completed'
                    pendingPrograms: stats.pendingPrograms || 0, // From programs table where status = 'pending'
                    inProgressPrograms: stats.inProgressPrograms || 0 // From programs table where status = 'in-progress'
                });

                console.log('Dashboard data loaded from database:', {
                    totalPrograms: stats.totalPrograms, // From programs table
                    completedPrograms: stats.completedPrograms, // From programs table
                    pendingPrograms: stats.pendingPrograms, // From programs table
                    inProgressPrograms: stats.inProgressPrograms, // From programs table
                    allPrograms: stats.programs
                });

                // Debug: Check inProgressPrograms specifically
                console.log('🔍 Dashboard: inProgressPrograms value:', stats.inProgressPrograms);
                console.log('🔍 Dashboard: dashboardData after set:', {
                    inProgressPrograms: stats.inProgressPrograms
                });

                // Set recent programs (latest 4 programs) with real progress calculation
                console.log('🔍 Dashboard: All programs from stats:', stats.programs);
                const recentProgramsData = await Promise.all((stats.programs || []).slice(0, 4).map(async (program: any) => {
                    console.log('🔍 Dashboard: Processing program:', { id: program.id, title: program.title, name: program.name, status: program.status });
                    // Calculate real progress based on task completion
                    let realProgress = 0;
                    try {
                        // Get program progress data to calculate real progress
                        console.log(`🔍 Dashboard: Fetching progress for program ${program.id} (${program.title})`);
                        const progressResponse = await databaseService.getProgramProgressTasks(program.id);
                        console.log(`📊 Dashboard: Progress response for program ${program.id}:`, progressResponse);

                        // Extract the actual data array from the response
                        const progressData = progressResponse && progressResponse.data ? progressResponse.data : progressResponse;
                        console.log(`📊 Dashboard: Extracted progress data for program ${program.id}:`, progressData);

                        if (progressData && Array.isArray(progressData) && progressData.length > 0) {
                            const completedTasks = progressData.filter((task: any) => task.status === 'completed').length;
                            const totalTasks = progressData.length;
                            realProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
                            console.log(`📊 Dashboard: Program ${program.id} progress calculation: ${completedTasks}/${totalTasks} = ${realProgress}%`);
                        } else {
                            console.log(`⚠️ Dashboard: No progress data found for program ${program.id} - data:`, progressData, 'isArray:', Array.isArray(progressData));
                        }
                    } catch (error) {
                        console.warn(`❌ Dashboard: Failed to fetch progress for program ${program.id}:`, error);
                        // Fallback to status-based progress if API fails
                        realProgress = program.status === 'completed' ? 100 :
                            program.status === 'in_progress' ? 50 : 0;
                        console.log(`🔄 Dashboard: Using fallback progress for program ${program.id}: ${realProgress}%`);
                    }

                    return {
                        id: program.id,
                        title: program.title || program.name || 'Untitled Program',
                        name: program.title || program.name || 'Untitled Program',
                        status: program.status === 'completed' ? 'completed' :
                            program.status === 'in_progress' ? 'in_progress' :
                                program.status === 'pending' ? 'pending' : 'on_hold',
                        progress: realProgress, // Real progress calculated from task completion
                        dueDate: program.dueDate || program.scheduleDate || 'Not set',
                    };
                }));
                console.log('🔍 Dashboard: Recent programs data:', recentProgramsData);
                setRecentPrograms(recentProgramsData);

                // Fetch real recent activities from database
                try {
                    console.log('🔄 Dashboard: Fetching real recent activities...');
                    const realActivities = await databaseService.getRecentActivities();
                    console.log('📊 Dashboard: Real activities received:', realActivities);

                    if (realActivities && realActivities.length > 0) {
                        // Use real activities from database
                        setRecentActivities(realActivities.slice(0, 4));
                    } else {
                        // Fallback: Generate activities from program data if no real activities
                        console.log('⚠️ Dashboard: No real activities found, using program data as fallback');
                        const activities: any[] = [];

                        // Add program activities (latest 4 programs)
                        const recentProgramsForActivity = (stats.programs || []).slice(0, 4);
                        recentProgramsForActivity.forEach((program: any) => {
                            // Calculate real time ago from createdAt or updatedAt
                            const getTimeAgo = (dateString: string) => {
                                if (!dateString) return 'Unknown time';

                                const now = new Date();
                                const programDate = new Date(dateString);
                                const diffInMs = now.getTime() - programDate.getTime();

                                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                                const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
                                const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

                                if (diffInMinutes < 1) return 'Just now';
                                if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
                                if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
                                if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
                                return programDate.toLocaleDateString();
                            };

                            // Use updatedAt if available, otherwise createdAt
                            const timeAgo = getTimeAgo(program.updatedAt || program.createdAt);

                            let message = '';
                            let icon = <FolderIcon />;
                            let type = 'success';

                            if (program.status === 'completed') {
                                message = `Program "${program.title}" <strong style="color: #4caf50;">completed</strong> successfully`;
                                icon = <CheckCircleIcon />;
                                type = 'success';
                            } else if (program.status === 'in_progress') {
                                message = `Program "${program.title}" moved to <strong style="color: #2196f3;">In Progress</strong>`;
                                icon = <PlayCircleIcon />;
                                type = 'info';
                            } else if (program.status === 'pending') {
                                message = `Program "${program.title}" is <strong style="color: #ff9800;">pending</strong> review`;
                                icon = <Pending />;
                                type = 'warning';
                            }

                            activities.push({
                                id: `program_${program.id}`,
                                icon,
                                message,
                                time: timeAgo,
                                type
                            });
                        });

                        // Add user activities (latest 2 users)
                        const recentUsers = (stats.users || []).slice(0, 2);
                        recentUsers.forEach((user: any) => {
                            const getTimeAgo = (dateString: string) => {
                                if (!dateString) return 'Unknown time';

                                const now = new Date();
                                const userDate = new Date(dateString);
                                const diffInMs = now.getTime() - userDate.getTime();

                                const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
                                const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
                                const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

                                if (diffInMinutes < 1) return 'Just now';
                                if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
                                if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
                                if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
                                return userDate.toLocaleDateString();
                            };

                            const timeAgo = getTimeAgo(user.createdAt);
                            activities.push({
                                id: `user_${user.id}`,
                                icon: <Person />,
                                message: `New user ${user.name || user.email} <strong style="color: #4caf50;">registered</strong> successfully`,
                                time: timeAgo,
                                type: 'success'
                            });
                        });

                        // Sort by time (most recent first) and take first 4
                        setRecentActivities(activities.slice(0, 4));
                    }
                } catch (error) {
                    console.error('❌ Dashboard: Error fetching recent activities:', error);
                    // Fallback to empty activities if API fails
                    setRecentActivities([]);
                }

            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    // Fetch holidays and birthdays for calendar
    useEffect(() => {
        const fetchCalendarData = async () => {
            try {
                const [holidaysRes, birthdaysRes] = await Promise.all([
                    getUpcomingHolidays(5),
                    getUpcomingBirthdays()
                ]);

                if (holidaysRes.success && holidaysRes.data) {
                    setHolidays(holidaysRes.data);
                }
                if (birthdaysRes.success && birthdaysRes.data) {
                    setBirthdays(birthdaysRes.data);
                }
            } catch (error) {
                console.error('Error fetching calendar data:', error);
            }
        };

        fetchCalendarData();
    }, []);

    // Helper to format date string
    const formatHolidayDate = (dateStr: string): string => {
        const cleanDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
        const [year, month, day] = cleanDate.split('-').map(Number);
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const date = new Date(year, month - 1, day, 12, 0, 0);
        return `${weekdays[date.getDay()]}, ${months[month - 1]} ${day}`;
    };

    // Calendar events from holidays and birthdays
    const calendarEvents = [
        ...holidays.map(h => ({
            date: h.date.includes('T') ? h.date.split('T')[0] : h.date,
            type: 'holiday' as const,
            title: h.name
        })),
        ...birthdays.map(b => ({
            date: b.birthday_this_year,
            type: 'birthday' as const,
            title: `${b.name}'s Birthday`
        }))
    ];

    const programStatusData = [
        { name: 'Completed', value: dashboardData.completedPrograms, color: '#4caf50' },
        { name: 'In Progress', value: dashboardData.inProgressPrograms, color: '#2196f3' },
        { name: 'Pending', value: dashboardData.pendingPrograms, color: '#ff9800' }
    ];

    const programStatusBarData = [
        { status: 'Completed', count: dashboardData.completedPrograms, color: '#4caf50' },
        { status: 'In Progress', count: dashboardData.inProgressPrograms, color: '#2196f3' },
        { status: 'Pending', count: dashboardData.pendingPrograms, color: '#ff9800' }
    ];

    // Real data from API - fetching from users and programs tables
    const stats = [
        { title: 'Total Programs', value: dashboardData.totalPrograms.toString(), icon: <FolderIcon />, color: '#2196f3' },
        { title: 'Completed Programs', value: dashboardData.completedPrograms.toString(), icon: <CheckCircleIcon />, color: '#ff9800' },
        { title: 'Pending Programs', value: dashboardData.pendingPrograms.toString(), icon: <Pending />, color: '#f44336' },
        { title: 'In Progress Programs', value: dashboardData.inProgressPrograms.toString(), icon: <PlayCircleIcon />, color: '#9c27b0' }
    ];

    // Debug: Log stats array
    console.log('🔍 Dashboard: Stats array:', stats);
    console.log('🔍 Dashboard: dashboardData state:', dashboardData);


    // Recent activities now come from real data via recentActivities state

    // Recent programs now come from real data via recentPrograms state


    // System status now comes from real data via systemStatus state



    const getActivityIconColor = (type: string) => {
        switch (type) {
            case 'success': return '#4caf50';
            case 'warning': return '#ff9800';
            case 'error': return '#f44336';
            default: return '#2196f3';
        }
    };

    // Function to get proper icon for activity based on action and activityType
    const getActivityIcon = (activity: any) => {
        const action = activity.action || activity.activityType || 'default';

        switch (action) {
            // User activities
            case 'user_registration': return <PersonAdd sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'profile_update': return <Edit sx={{ color: '#2196f3', fontSize: '20px' }} />;

            // Task activities
            case 'project_completion': return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'task_assignment': return <Assignment sx={{ color: '#ff9800', fontSize: '20px' }} />;
            case 'task_update': return <Edit sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'task_management': return <TaskAlt sx={{ color: '#9c27b0', fontSize: '20px' }} />;

            // Task status changes
            case 'started': return <PlayArrow sx={{ color: '#ff9800', fontSize: '20px' }} />;
            case 'completed': return <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'reopened': return <Refresh sx={{ color: '#ff5722', fontSize: '20px' }} />;
            case 'cancelled': return <Cancel sx={{ color: '#f44336', fontSize: '20px' }} />;

            // System activities
            case 'system_maintenance': return <Build sx={{ color: '#607d8b', fontSize: '20px' }} />;
            case 'database_backup': return <Security sx={{ color: '#795548', fontSize: '20px' }} />;
            case 'system_alert': return <Warning sx={{ color: '#f44336', fontSize: '20px' }} />;
            case 'performance_optimization': return <TrendingUp sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'security_audit': return <Security sx={{ color: '#9c27b0', fontSize: '20px' }} />;

            // Reports and analytics
            case 'report_generation': return <Assessment sx={{ color: '#3f51b5', fontSize: '20px' }} />;

            // Workshop and session activities
            case 'workshop_completion': return <School sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'workshop_scheduled': return <ScheduleIcon sx={{ color: '#ff9800', fontSize: '20px' }} />;
            case 'session_start': return <Event sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'workshop_tasks': return <GroupWork sx={{ color: '#9c27b0', fontSize: '20px' }} />;
            case 'session_tasks': return <Work sx={{ color: '#3f51b5', fontSize: '20px' }} />;

            // Program management
            case 'program_management': return <Business sx={{ color: '#607d8b', fontSize: '20px' }} />;
            case 'program_created': return <Add sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'program_updated': return <Update sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'program_deleted': return <Delete sx={{ color: '#f44336', fontSize: '20px' }} />;

            // Feature and development
            case 'feature_deployment': return <Settings sx={{ color: '#00bcd4', fontSize: '20px' }} />;
            case 'feature_update': return <Update sx={{ color: '#ff9800', fontSize: '20px' }} />;

            // File and document activities
            case 'file_upload': return <Upload sx={{ color: '#4caf50', fontSize: '20px' }} />;
            case 'file_download': return <Download sx={{ color: '#2196f3', fontSize: '20px' }} />;
            case 'document_created': return <Description sx={{ color: '#9c27b0', fontSize: '20px' }} />;
            case 'document_updated': return <Edit sx={{ color: '#ff9800', fontSize: '20px' }} />;

            // Default fallback
            default: return <Info sx={{ color: '#757575', fontSize: '20px' }} />;
        }
    };

    // getPriorityColor function removed - priority not available in database

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return '#4caf50';
            case 'in_progress': return '#2196f3';
            case 'pending': return '#ff9800';
            case 'on_hold': return '#9e9e9e';
            default: return '#757575';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed': return <CheckCircleIcon />;
            case 'in_progress': return <PlayCircleIcon />;
            case 'pending': return <ScheduleIcon />;
            case 'on_hold': return <PauseCircleIcon />;
            default: return <FolderIcon />;
        }
    };

    const getProgressBarColor = (status: string, progress: number) => {
        switch (status) {
            case 'completed': return '#4caf50'; // Green for completed
            case 'in_progress': return '#2196f3'; // Blue for in-progress
            case 'pending': return '#ff9800'; // Orange for pending
            case 'on_hold': return '#9e9e9e'; // Gray for on hold
            default: return '#2196f3'; // Blue default
        }
    };


    return (
        <Layout>
            <Box sx={{
                color: theme.palette.text.primary,
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
                p: { xs: 1, sm: 2, md: 3, lg: 4 },
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    mb: { xs: 2, sm: 3, md: 4 },
                    textAlign: { xs: 'center', sm: 'left' },
                    px: { xs: 1, sm: 0 }
                }}>
                    <Typography variant="h3" sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 'bold',
                        mb: 1,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
                        lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                        wordBreak: 'break-word'
                    }}>
                        Program Manager
                    </Typography>
                    <Typography variant="h6" sx={{
                        color: theme.palette.text.secondary,
                        fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem', lg: '1.5rem' },
                        lineHeight: { xs: 1.3, sm: 1.4, md: 1.5 },
                        maxWidth: { xs: '100%', sm: '80%', md: '70%', lg: '60%' },
                        wordBreak: 'break-word'
                    }}>
                        Manage users, programs, and system operations
                    </Typography>
                </Box>

                {/* Key Performance Indicators */}
                <Box sx={{ mb: { xs: 3, sm: 4, md: 6 } }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: '200px'
                        }}>
                            <CircularProgress size={60} />
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(2, 1fr)',
                                lg: 'repeat(4, 1fr)'
                            },
                            gap: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                            width: '100%',
                            px: { xs: 0.5, sm: 0 }
                        }}>
                            {stats.map((stat, index) => (
                                <Card key={index} sx={{
                                    background: theme.palette.mode === 'dark'
                                        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                                        : 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: { xs: 2, sm: 3 },
                                    overflow: 'hidden',
                                    position: 'relative',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        transition: 'all 0.3s ease',
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 12px 40px rgba(0, 0, 0, 0.3)'
                                            : '0 12px 40px rgba(0, 0, 0, 0.1)'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: `linear-gradient(90deg, ${stat.color} 0%, ${stat.color}80 100%)`
                                    }
                                }}>
                                    <CardContent sx={{
                                        p: { xs: 2.5, sm: 3, md: 3.5 },
                                        height: '100%',
                                        minHeight: { xs: '120px', sm: '140px', md: '160px' },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    }}>
                                        <Box display="flex" alignItems="center" gap={{ xs: 2.5, sm: 3, md: 3.5 }} sx={{ height: '100%' }}>
                                            <Box sx={{
                                                width: { xs: 48, sm: 56, md: 64 },
                                                height: { xs: 48, sm: 56, md: 64 },
                                                borderRadius: 2.5,
                                                background: `linear-gradient(135deg, ${stat.color}20 0%, ${stat.color}10 100%)`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: `1px solid ${stat.color}30`,
                                                flexShrink: 0,
                                                boxShadow: `0 4px 12px ${stat.color}15`
                                            }}>
                                                {stat.icon}
                                            </Box>
                                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start' }}>
                                                <Typography variant="h3" sx={{
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 700,
                                                    mb: 1,
                                                    fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem', lg: '2.5rem' },
                                                    lineHeight: 1.1,
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {stat.value}
                                                </Typography>
                                                <Typography variant="body1" sx={{
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(26, 26, 46, 0.8)',
                                                    fontWeight: 500,
                                                    fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                                                    lineHeight: 1.3
                                                }}>
                                                    {stat.title}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>


                {/* Projects & Tasks Section */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' },
                    gap: { xs: 2, sm: 3, md: 4 },
                    mb: { xs: 3, sm: 4, md: 6 },
                    px: { xs: 0.5, sm: 0 }
                }}>
                    {/* Recent Programs */}
                    <Card sx={{
                        background: isDarkMode
                            ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
                            : 'linear-gradient(135deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode
                            ? '1px solid rgba(255, 255, 255, 0.2)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{
                                p: { xs: 2, sm: 3 },
                                borderBottom: isDarkMode
                                    ? '1px solid rgba(255, 255, 255, 0.1)'
                                    : '1px solid rgba(0, 0, 0, 0.1)',
                                background: isDarkMode
                                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                                    : 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                            }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                    <Typography variant="h6" sx={{
                                        color: theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        Recent Programs
                                    </Typography>
                                    <Box display="flex" gap={1}>
                                        <Button
                                            size="small"
                                            onClick={() => navigate('/sessions')}
                                            sx={{
                                                color: '#2196f3',
                                                fontWeight: 500,
                                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                                border: '1px solid rgba(33, 150, 243, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                                                    transform: 'translateY(-1px)'
                                                }
                                            }}
                                        >
                                            Sessions
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => navigate('/workshops')}
                                            sx={{
                                                color: '#ff9800',
                                                fontWeight: 500,
                                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                                                border: '1px solid rgba(255, 152, 0, 0.3)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 152, 0, 0.2)',
                                                    transform: 'translateY(-1px)'
                                                }
                                            }}
                                        >
                                            Workshops
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>
                            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                {recentPrograms.map((project: any, index: number) => {
                                    console.log('🔍 Dashboard: Rendering project:', { id: project.id, title: project.title, name: project.name, status: project.status });
                                    return (
                                        <Box key={project.id} sx={{
                                            p: { xs: 1.5, sm: 2 },
                                            borderRadius: 2,
                                            mb: index < recentPrograms.length - 1 ? 1 : 0,
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                                transition: 'all 0.2s ease'
                                            }
                                        }}>
                                            <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }} mb={1.5}>
                                                <Box sx={{
                                                    width: { xs: 32, sm: 36 },
                                                    height: { xs: 32, sm: 36 },
                                                    borderRadius: 2,
                                                    background: `linear-gradient(135deg, ${getStatusColor(project.status)}25 0%, ${getStatusColor(project.status)}15 100%)`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: `2px solid ${getStatusColor(project.status)}40`,
                                                    boxShadow: `0 2px 8px ${getStatusColor(project.status)}20`
                                                }}>
                                                    {React.cloneElement(getStatusIcon(project.status), {
                                                        sx: {
                                                            color: getStatusColor(project.status),
                                                            fontSize: { xs: 18, sm: 20 },
                                                            fontWeight: 'bold'
                                                        }
                                                    })}
                                                </Box>
                                                <Box flex={1} sx={{ minWidth: 0 }}>
                                                    <Typography variant="h6" sx={{
                                                        color: isDarkMode ? 'white' : theme.palette.text.primary,
                                                        fontWeight: 700,
                                                        mb: 0.5,
                                                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        textShadow: isDarkMode ? '0 1px 2px rgba(0,0,0,0.3)' : 'none'
                                                    }}>
                                                        {project.title || project.name || 'Untitled Program'}
                                                    </Typography>
                                                    <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                                                        <Chip
                                                            label={project.status === 'in_progress' ? 'In Progress' :
                                                                project.status === 'completed' ? 'Completed' :
                                                                    project.status === 'pending' ? 'Pending' : project.status}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: `${getStatusColor(project.status)}25`,
                                                                color: getStatusColor(project.status),
                                                                border: `1px solid ${getStatusColor(project.status)}40`,
                                                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                                height: { xs: 20, sm: 22 },
                                                                fontWeight: 600,
                                                                textTransform: 'capitalize',
                                                                boxShadow: `0 1px 3px ${getStatusColor(project.status)}20`
                                                            }}
                                                        />
                                                        {/* Priority chip removed - not available in database */}
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={0.5}>
                                                        <Typography variant="caption" sx={{
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.secondary,
                                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                            fontWeight: 600,
                                                            backgroundColor: `${getStatusColor(project.status)}15`,
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            border: `1px solid ${getStatusColor(project.status)}30`
                                                        }}>
                                                            📊 Progress: {project.progress || 0}%
                                                        </Typography>
                                                        <Typography variant="caption" sx={{
                                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.secondary,
                                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                            fontWeight: 600,
                                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)'
                                                        }}>
                                                            📅 Due: {formatDate(project.dueDate)}
                                                        </Typography>
                                                    </Box>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={project.progress || 0}
                                                        sx={{
                                                            height: { xs: 6, sm: 8 },
                                                            borderRadius: 4,
                                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                                                            border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                                                            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
                                                            '& .MuiLinearProgress-bar': {
                                                                background: `linear-gradient(90deg, ${getProgressBarColor(project.status, project.progress || 0)} 0%, ${getProgressBarColor(project.status, project.progress || 0)}90 100%)`,
                                                                borderRadius: 4,
                                                                boxShadow: `0 1px 3px ${getProgressBarColor(project.status, project.progress || 0)}40`
                                                            }
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Recent Activity */}
                    <Card sx={{
                        background: isDarkMode ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)' : theme.palette.background.paper,
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${theme.palette.divider}`,
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{
                                p: { xs: 2, sm: 3 },
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                                background: isDarkMode ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)' : 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(139, 195, 74, 0.05) 100%)'
                            }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                    <Typography variant="h6" sx={{
                                        color: isDarkMode ? 'white' : theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        Recent Activity
                                    </Typography>
                                    <Button
                                        size="small"
                                        onClick={() => navigate('/recent-activities')}
                                        sx={{
                                            color: '#4caf50',
                                            fontWeight: 500,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                                        }}
                                    >
                                        View All
                                    </Button>
                                </Box>
                            </Box>
                            <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                                {recentActivities.map((activity: any, index: number) => (
                                    <Box key={activity.id} sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: { xs: 1.5, sm: 2 },
                                        p: { xs: 1.5, sm: 2 },
                                        borderRadius: 2,
                                        mb: index < recentActivities.length - 1 ? 1 : 0,
                                        '&:hover': {
                                            backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.action.hover,
                                            transition: 'all 0.2s ease'
                                        }
                                    }}>
                                        <Box sx={{
                                            width: { xs: 28, sm: 32 },
                                            height: { xs: 28, sm: 32 },
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${getActivityIconColor(activity.type)}20 0%, ${getActivityIconColor(activity.type)}10 100%)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: `1px solid ${getActivityIconColor(activity.type)}30`,
                                            flexShrink: 0
                                        }}>
                                            {getActivityIcon(activity)}
                                        </Box>
                                        <Box flex={1} sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{
                                                color: isDarkMode ? 'white' : theme.palette.text.primary,
                                                fontWeight: 500,
                                                mb: 0.5,
                                                lineHeight: 1.4,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                '& strong': {
                                                    fontWeight: 700,
                                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                                }
                                            }}
                                                dangerouslySetInnerHTML={{
                                                    __html: activity.message
                                                }}
                                            />
                                            <Typography variant="caption" sx={{
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : theme.palette.text.secondary,
                                                fontSize: { xs: '0.7rem', sm: '0.75rem' }
                                            }}>
                                                {activity.time}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>

                </Box>


                {/* Programs by Status Chart */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr' },
                    gap: { xs: 2, sm: 3, md: 4 },
                    mb: { xs: 3, sm: 4, md: 6 }
                }}>

                    {/* Projects by Status Chart */}
                    <Card sx={{
                        background: isDarkMode ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)' : theme.palette.background.paper,
                        backdropFilter: 'blur(10px)',
                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : `1px solid ${theme.palette.divider}`,
                        borderRadius: { xs: 2, sm: 3 },
                        overflow: 'hidden'
                    }}>
                        <CardContent sx={{ p: 0 }}>
                            <Box sx={{
                                p: { xs: 2, sm: 3 },
                                borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : `1px solid ${theme.palette.divider}`,
                                background: isDarkMode ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(139, 195, 74, 0.1) 100%)' : 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(139, 195, 74, 0.05) 100%)'
                            }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                                    <Typography variant="h6" sx={{
                                        color: isDarkMode ? 'white' : theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: { xs: '1rem', sm: '1.25rem' }
                                    }}>
                                        Programs by Status
                                    </Typography>
                                    <IconButton size="small" sx={{
                                        color: '#4CAF50',
                                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                        '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.2)' }
                                    }}>
                                        <BarChart />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{
                                    height: { xs: 280, sm: 350 },
                                    background: isDarkMode
                                        ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(139, 195, 74, 0.05) 100%)'
                                        : 'linear-gradient(135deg, rgba(76, 175, 80, 0.02) 0%, rgba(139, 195, 74, 0.02) 100%)',
                                    borderRadius: 2,
                                    border: isDarkMode
                                        ? '1px solid rgba(76, 175, 80, 0.2)'
                                        : '1px solid rgba(76, 175, 80, 0.1)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RechartsBarChart data={programStatusBarData} margin={{ top: 20, right: 140, left: 20, bottom: 20 }}>
                                            <CartesianGrid
                                                strokeDasharray="3 3"
                                                stroke={isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
                                            />
                                            <XAxis
                                                dataKey="status"
                                                stroke={isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'}
                                                fontSize={12}
                                            />
                                            <YAxis
                                                stroke={isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'}
                                                fontSize={12}
                                                label={{
                                                    value: 'Programs',
                                                    angle: -90,
                                                    position: 'insideLeft',
                                                    style: {
                                                        textAnchor: 'middle',
                                                        fill: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                                                        fontSize: '12px',
                                                        fontWeight: 500
                                                    }
                                                }}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: isDarkMode ? 'rgba(26, 31, 58, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                                                    border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                                                    borderRadius: '8px',
                                                    color: isDarkMode ? 'white' : 'black'
                                                }}
                                            />
                                            <Legend
                                                wrapperStyle={{
                                                    color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                                                    fontSize: '12px'
                                                }}
                                                formatter={(value: string) => {
                                                    return value === 'count' ? 'Program' : value;
                                                }}
                                            />
                                            <Bar
                                                dataKey="count"
                                                name="Program"
                                                radius={[4, 4, 0, 0]}
                                            >
                                                {programStatusBarData.map((entry: any, index: number) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={
                                                            entry.status === 'Completed' ? '#4caf50' : // Green
                                                                entry.status === 'In Progress' ? '#2196f3' : // Blue
                                                                    entry.status === 'Pending' ? '#ffeb3b' : // Yellow
                                                                        '#ff9800' // Orange fallback
                                                        }
                                                    />
                                                ))}
                                            </Bar>
                                        </RechartsBarChart>
                                    </ResponsiveContainer>

                                    {/* Color Legend */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 3,
                                        mt: 2,
                                        flexWrap: 'wrap'
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: '#4CAF50',
                                                borderRadius: '2px'
                                            }} />
                                            <Typography variant="caption" sx={{
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                                                fontSize: '11px',
                                                fontWeight: 500
                                            }}>
                                                Completed
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: '#2196F3',
                                                borderRadius: '2px'
                                            }} />
                                            <Typography variant="caption" sx={{
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                                                fontSize: '11px',
                                                fontWeight: 500
                                            }}>
                                                In Progress
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{
                                                width: 12,
                                                height: 12,
                                                backgroundColor: '#FF9800',
                                                borderRadius: '2px'
                                            }} />
                                            <Typography variant="caption" sx={{
                                                color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)',
                                                fontSize: '11px',
                                                fontWeight: 500
                                            }}>
                                                Pending
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Overlay Stats */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        textAlign: 'right',
                                        zIndex: 10,
                                        backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.95)',
                                        padding: '12px 16px',
                                        borderRadius: '12px',
                                        backdropFilter: 'blur(10px)',
                                        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
                                        boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                        minWidth: '120px'
                                    }}>
                                        <Typography variant="h4" sx={{
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : theme.palette.text.primary,
                                            fontWeight: 700,
                                            fontSize: { xs: '1.5rem', sm: '2rem' },
                                            lineHeight: 1
                                        }}>
                                            {dashboardData.totalPrograms}
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                                            fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                            fontWeight: 500
                                        }}>
                                            Total Programs
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                                            <Typography variant="body2" sx={{
                                                color: '#4caf50',
                                                fontWeight: 600,
                                                fontSize: { xs: '0.7rem', sm: '0.8rem' }
                                            }}>
                                                {Math.round((dashboardData.completedPrograms / dashboardData.totalPrograms) * 100)}% Complete
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Calendar & Holidays Row */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                    gap: 3,
                    mb: 4
                }}>
                    {/* Mini Calendar */}
                    <MiniCalendar
                        events={calendarEvents}
                        onDateClick={(date) => console.log('Clicked date:', date)}
                    />

                    {/* Upcoming Holidays */}
                    <Card sx={{
                        backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                        borderRadius: 2,
                        border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Event sx={{ color: theme.palette.error.main }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Upcoming Holidays
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    endIcon={<ArrowForward />}
                                    onClick={() => navigate('/holidays')}
                                    sx={{ textTransform: 'none' }}
                                >
                                    View All
                                </Button>
                            </Box>

                            <List sx={{ p: 0 }}>
                                {holidays.length > 0 ? holidays.slice(0, 4).map((holiday, index) => (
                                    <React.Fragment key={holiday.id}>
                                        <ListItem sx={{ px: 0, py: 1 }}>
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <CalendarMonth sx={{ color: theme.palette.error.main }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={holiday.name}
                                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                                secondary={formatHolidayDate(holiday.date)}
                                                secondaryTypographyProps={{ variant: 'caption' }}
                                            />
                                            <Chip
                                                label={holiday.type}
                                                size="small"
                                                sx={{
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    backgroundColor: holiday.type === 'public' ? '#F4433620' : '#FF980020',
                                                    color: holiday.type === 'public' ? '#F44336' : '#FF9800'
                                                }}
                                            />
                                        </ListItem>
                                        {index < Math.min(holidays.length, 4) - 1 && <Divider />}
                                    </React.Fragment>
                                )) : (
                                    <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography variant="body2">No upcoming holidays</Typography>
                                    </Box>
                                )}
                            </List>
                        </CardContent>
                    </Card>
                </Box>

            </Box>
        </Layout>
    );
};

export default ProgramManagerDashboard;
